import { Directive, ElementRef, Input, OnInit, inject } from '@angular/core';
import {Router} from '@angular/router';
import {FilterState} from '../../generic/classes/filter.state';
import {ProductPage} from '../../generic/classes/product.page';
import {Logger} from '../../generic/logging/default-log.service';
import {FilterService} from '../../generic/service/filter.service';
import {ProductService} from '../../generic/service/product.service';
import {UserService} from '../../generic/service/user.service';

/**
 * The base class for the related products type components that are used in product detail. We currently have two variants of this component
 * - Grower products: Shows a list of products related to the same grower as the currently viewed product
 * - Alternative products: Shows a list of alternative available products based on the currently viewed product
 *
 * {@link GrowerProductsComponent}
 * {@link AlternativeProductsComponent}
 *
 * @author Patrick van Zadel
 */
@Directive()
export class RelatedProductsComponent implements OnInit {
	productService = inject(ProductService);
	domElement = inject(ElementRef);
	filterService = inject(FilterService);
	router = inject(Router);
	logger = inject(Logger);

	@Input() limit: number;

	public filterStateUrl: string;
	public productOverviewUrl: string;

	public productPage: ProductPage;

	public readonly _userService: UserService;

	constructor() {
		const userService = inject(UserService);

		this._userService = userService;
	}

	get userService(): UserService {
		return this._userService;
	}

	ngOnInit(): void {
		const filterState = new FilterState([]);

		// @Input parameters aren't available in the constructor yet
		this.filterService.initFilterStateForRequestParams(
			filterState,
			this.router.parseUrl(this.filterStateUrl).queryParams,
		);

		this.filterService.initSelectedLanguage(filterState);

		this.loadProducts(filterState);
	}

	public loadProducts(filterState: FilterState) {
		this.productService
			.getProducts(filterState.getParams())
			.then(productPage => {
				this.productPage = productPage;
			})
			.catch(error => {
				this.logger.error(
					'Error loading the products from the server',
					error,
				);
			});
	}

	public viewProductDetail(detailUrl: string) {
		this.router.navigateByUrl(detailUrl);
	}

	public getShowAllProductsUrl() {
		return this.router.navigateByUrl(this.productOverviewUrl);
	}
}
